import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar as CalendarIcon, Info } from "lucide-react";
import { api, MARKET_META } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import {
    SEO_MARKETS_BY_KEY,
    buildPreviousResultCopy,
    previousResultPath,
    resultTodayPath,
    commonNumberPath,
} from "../lib/seoMarkets";

const SITE_URL = "https://shillongmorningteerresulttoday.com";

const prettyDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const todayIST = () =>
    new Date(Date.now() - 60 * 60 * 1000).toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
    });

const GamePreviousResult = ({ marketKey }) => {
    const meta = SEO_MARKETS_BY_KEY[marketKey];
    const cardMeta = MARKET_META[marketKey];
    const copy = useMemo(() => buildPreviousResultCopy(meta), [meta]);
    const slug = meta.urlPrefix;
    const path = previousResultPath(slug);

    useSeo({
        title: copy.title,
        description: copy.description,
        keywords: copy.keywords,
        path,
        image: `${SITE_URL}/og-image.png`,
        imageAlt: `${meta.display} Previous Result – FR and SR archive`,
        breadcrumbs: [
            { name: meta.display, path: resultTodayPath(slug) },
            { name: "Previous Results", path },
        ],
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: copy.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    });

    const [dateFilter, setDateFilter] = useState("");
    const [yearFilter, setYearFilter] = useState(""); // e.g. "2024" — client-side filter
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const dateStr = todayIST();

    const load = async () => {
        setLoading(true);
        try {
            const params = { market_key: marketKey, limit: 3000 };
            if (dateFilter) params.date = dateFilter;
            const { data } = await api.get("/results", { params });
            // Exclude today's row (that's covered by Result Today page)
            setRows(data.filter((r) => r.date !== dateStr));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marketKey, dateFilter]);

    // Available years for filter — from ALL loaded rows
    const availableYears = useMemo(() => {
        const set = new Set(rows.map((r) => r.date?.slice(0, 4)).filter(Boolean));
        return Array.from(set).sort((a, b) => b.localeCompare(a));
    }, [rows]);

    // Client-side year filter (works on top of loaded rows without extra API call)
    const visibleRows = useMemo(() => {
        if (!yearFilter) return rows;
        return rows.filter((r) => r.date?.startsWith(yearFilter));
    }, [rows, yearFilter]);

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-8" data-testid={`page-previous-${slug}`}>
            <Breadcrumbs
                items={[
                    { name: meta.display, path: resultTodayPath(slug) },
                    { name: "Previous Results", path },
                ]}
            />

            {/* Hero */}
            <header className="mt-4 rounded-2xl overflow-hidden card-crisp">
                <div
                    className="px-5 py-4 md:px-8 md:py-6 text-white"
                    style={{ background: cardMeta?.accent || "#1E3A8A" }}
                >
                    <div className="text-[11px] uppercase tracking-widest opacity-90 font-semibold">
                        Archive
                    </div>
                    <h1
                        className="font-display font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight"
                        data-testid="page-h1"
                    >
                        {copy.h1}
                    </h1>
                    <p className="mt-1 text-white/90 text-sm">
                        FR &amp; SR result history for {meta.display} · newest first
                    </p>
                </div>
            </header>

            {/* Filters */}
            <section className="mt-6 card-crisp p-4 flex flex-col md:flex-row gap-3 md:items-center">
                <div className="flex-1 flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
                    <CalendarIcon className="h-4 w-4 text-slate-500" />
                    <input
                        type="date"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        data-testid="filter-date"
                        className="w-full bg-transparent outline-none text-sm font-num"
                    />
                    {dateFilter && (
                        <button
                            onClick={() => setDateFilter("")}
                            className="text-xs text-blue-900 font-semibold"
                            data-testid="clear-date-btn"
                        >
                            Clear
                        </button>
                    )}
                </div>
                {availableYears.length > 1 && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-500 font-semibold">Year:</span>
                        <button
                            onClick={() => setYearFilter("")}
                            data-testid="year-btn-all"
                            className={`px-3 py-1 rounded-md border text-xs font-semibold ${
                                yearFilter === "" ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-blue-900 hover:bg-slate-50"
                            }`}
                        >
                            All
                        </button>
                        {availableYears.map((y) => (
                            <button
                                key={y}
                                onClick={() => setYearFilter(yearFilter === y ? "" : y)}
                                data-testid={`year-btn-${y}`}
                                className={`px-3 py-1 rounded-md border text-xs font-semibold ${
                                    yearFilter === y ? "bg-blue-700 text-white border-blue-700" : "border-slate-200 text-blue-900 hover:bg-slate-50"
                                }`}
                            >
                                {y}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            {/* Table */}
            <section className="mt-6 card-crisp overflow-hidden">
                <div className="grid grid-cols-12 bg-blue-900 text-white text-xs uppercase tracking-widest font-semibold px-4 py-3">
                    <div className="col-span-6 md:col-span-6">Date</div>
                    <div className="col-span-3 text-right">F/R</div>
                    <div className="col-span-3 text-right">S/R</div>
                </div>
                {loading ? (
                    <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>
                ) : visibleRows.length === 0 ? (
                    <div className="p-10 text-center text-slate-400" data-testid="no-results-msg">
                        No previous {meta.display} results found. Try clearing the filter.
                    </div>
                ) : (
                    <ul>
                        {visibleRows.map((r) => (
                            <li
                                key={r.id}
                                className="grid grid-cols-12 px-4 py-3 items-center border-t border-slate-100 hover:bg-slate-50"
                                data-testid={`row-${r.id}`}
                            >
                                <div className="col-span-6 md:col-span-6 font-num text-sm text-slate-700">
                                    {prettyDate(r.date)}
                                </div>
                                <div className="col-span-3 text-right result-digit text-lg text-blue-900">
                                    {r.fr ?? "XX"}
                                </div>
                                <div className="col-span-3 text-right result-digit text-lg text-blue-900">
                                    {r.sr ?? "XX"}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* About */}
            <section className="mt-8">
                <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight">
                    About the {meta.display} Archive
                </h2>
                <p className="text-slate-700 mt-2 leading-relaxed">
                    This page lists every {meta.display} result that has been published on this website. Each
                    row shows the calendar date, the First Round (F/R) winning number and the Second Round
                    (S/R) winning number. The list is refreshed every time an admin publishes a new{" "}
                    {meta.display} result.
                </p>
                <p className="text-slate-700 mt-3 leading-relaxed">
                    Use the date picker above to jump straight to a specific {meta.display} result date. If
                    a row shows <em>XX</em> for either F/R or S/R it means that round was not declared for
                    that particular day.
                </p>
            </section>

            {/* Cross-links */}
            <section className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <CrossLink
                    to={resultTodayPath(slug)}
                    title={`${meta.display} Result Today`}
                    desc="Live FR & SR result"
                    testid="cta-result-today"
                />
                <CrossLink
                    to={commonNumberPath(slug)}
                    title={`${meta.display} Common Number`}
                    desc="Direct · House · Ending — daily update"
                    testid="cta-common-number"
                />
            </section>

            {/* FAQ */}
            <section className="mt-10">
                <h2 className="font-display font-bold text-xl md:text-2xl text-blue-950 tracking-tight">
                    Frequently Asked Questions
                </h2>
                <div className="mt-3 space-y-3">
                    {copy.faq.map((f, i) => (
                        <details
                            key={i}
                            className="card-crisp p-4 open:shadow-md"
                            data-testid={`faq-${i}`}
                        >
                            <summary className="cursor-pointer font-semibold text-blue-950">
                                {f.q}
                            </summary>
                            <p className="mt-2 text-slate-700 leading-relaxed text-sm">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            <div className="mt-8 rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900 flex gap-3">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p>
                    {meta.display} previous results are shown only for reference. Please follow all local
                    laws applicable to you.
                </p>
            </div>

            <div className="mt-6 text-center">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-blue-900 hover:underline"
                >
                    ← Back to All Teer Results
                </Link>
            </div>
        </div>
    );
};

const CrossLink = ({ to, title, desc, testid }) => (
    <Link
        to={to}
        className="card-crisp p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid={testid}
    >
        <div>
            <div className="font-display font-bold text-blue-950 tracking-tight">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
        </div>
        <ArrowRight className="h-4 w-4 text-blue-900" />
    </Link>
);

export default GamePreviousResult;
